import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { PanelThemeProvider } from './theme';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AuthProvider } from './contexts/AuthContext';
import { SnackbarProvider } from 'notistack';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import MainLayout from './components/Layout/MainLayout';
import Login from './pages/Auth/Login';
import Dashboard from './pages/Dashboard';
import City from './pages/Adress/City';
import District from './pages/Adress/District';
import Products from './pages/Products/Products';
import Packaging from './pages/Products/Packaging';
import Staff from './pages/Staff';
import Addresses from './pages/Addresses';
import Settings from './pages/Settings';
import TelegramBots from './pages/TelegramBots/TelegramBots';
import BotUsers from './pages/TelegramBots/BotUsers';
import PrivateBotRequests from './pages/TelegramBots/PrivateBotRequests';
import TelegramBotsSettings from './pages/TelegramBots/Settings'
import TelegramBotsMessages from './pages/TelegramBots/Message'
import TelegramChannels from './pages/TelegramChannels';
import BroadcastsList from './pages/Broadcasts/BroadcastsList';
import CreateBroadcast from './pages/Broadcasts/CreateBroadcast';
import EditBroadcast from './pages/Broadcasts/EditBroadcast';
import TelegramUsers from './pages/TelegramUsers';
import Orders from './pages/Orders';
import Withdrawals from './pages/Withdrawals';
import BugTracker from './pages/BugTracker';
import CourierStats from './pages/CourierStats';
import CourierDetailStats from './pages/CourierDetailStats';
import CourierSalaries from './pages/CourierSalaries';
import Inventory from './pages/Inventory';
import ApkBuilder from './pages/ApkBuilder';
import CityStats from './pages/CityStats';
import UserBalanceHistory from './pages/UserBalanceHistory';
import RolePermissions from './pages/RolePermissions';
import ActivityLogs from './pages/ActivityLogs';
import Blacklist from './pages/Blacklist';
import ChangePassword from './pages/ChangePassword';
import SalesSiteSettings from './pages/SalesSite/Settings';
import SalesSiteDesign from './pages/SalesSite/Design';
import SalesSiteDomains from './pages/SalesSite/Domains';
import SalesSiteVisualEditor from './pages/SalesSite/VisualEditor';
import DepositAdmin from './pages/DepositAdmin';
import MapPoints from './pages/MapPoints';
import PermissionGuard from './components/ProtectedRoute';
import './App.css';

function App() {
  return (
    <AuthProvider>
      <PanelThemeProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <SnackbarProvider maxSnack={3} autoHideDuration={3000} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            
            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
              <Route element={<MainLayout />}>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/map-points" element={<MapPoints />} />
                <Route path="/cities" element={<PermissionGuard resource="cities" action="view"><City /></PermissionGuard>} />
                <Route path="/districts" element={<PermissionGuard resource="districts" action="view"><District /></PermissionGuard>} />
                <Route path="/products" element={<PermissionGuard resource="products" action="view"><Products /></PermissionGuard>} />
                <Route path="/packaging" element={<PermissionGuard resource="packagings" action="view"><Packaging /></PermissionGuard>} />
                <Route path="/staff" element={<PermissionGuard resource="users" action="view"><Staff /></PermissionGuard>} />
                <Route path="/addresses" element={<PermissionGuard resource="addresses" action="view"><Addresses /></PermissionGuard>} />
                <Route path="/adress/city" element={<City />} />
                <Route path="/settings" element={<PermissionGuard resource="settings" action="view"><Settings /></PermissionGuard>} />
                <Route path="/telegram-bots" element={<PermissionGuard resource="telegramBots" action="view"><TelegramBots /></PermissionGuard>} />
                <Route path="/telegram-bots/access-requests" element={<PermissionGuard resource="telegramBots" action="view" roles={['operator', 'curator']}><PrivateBotRequests /></PermissionGuard>} />
                <Route path="/telegram-bots/:id/users" element={<PermissionGuard resource="telegramBots" action="view"><BotUsers /></PermissionGuard>} />
                <Route path="/telegram-bots-settings" element={<PermissionGuard resource="telegramBots" action="settings"><TelegramBotsSettings /></PermissionGuard>} />
                <Route path="/telegram-bots-message" element={<PermissionGuard resource="telegramBots" action="edit"><TelegramBotsMessages /></PermissionGuard>} />
                <Route path="/telegram-channels" element={<PermissionGuard resource="telegramChannels" action="view"><TelegramChannels /></PermissionGuard>} />
                <Route path="/telegram-users" element={<PermissionGuard resource="telegramUsers" action="view"><TelegramUsers /></PermissionGuard>} />
                <Route path="/orders" element={<PermissionGuard resource="orders" action="view"><Orders /></PermissionGuard>} />
                <Route path="/withdrawals" element={<PermissionGuard resource="withdrawals" action="view"><Withdrawals /></PermissionGuard>} />
                <Route path="/bugtr" element={<PermissionGuard resource="bugTracker" action="view"><BugTracker /></PermissionGuard>} />
                <Route path="/courier-stats" element={<PermissionGuard resource="stats" action="courierStats"><CourierStats /></PermissionGuard>} />
                <Route path="/courier-stats/:courierId" element={<PermissionGuard resource="stats" action="courierStats"><CourierDetailStats /></PermissionGuard>} />
                <Route path="/courier-salaries" element={<PermissionGuard resource="salaries" action="view"><CourierSalaries /></PermissionGuard>} />
                <Route path="/inventory" element={<PermissionGuard resource="inventory" action="view"><Inventory /></PermissionGuard>} />
                <Route path="/apk-builder" element={<PermissionGuard resource="settings" action="edit"><ApkBuilder /></PermissionGuard>} />
                <Route path="/city-stats" element={<PermissionGuard resource="stats" action="cityStats"><CityStats /></PermissionGuard>} />
                <Route path="/user-balance-history" element={<PermissionGuard resource="telegramUsers" action="view"><UserBalanceHistory /></PermissionGuard>} />
                <Route path="/role-permissions" element={<PermissionGuard resource="roles" action="view"><RolePermissions /></PermissionGuard>} />
                <Route path="/activity-logs" element={<PermissionGuard resource="activityLogs" action="view"><ActivityLogs /></PermissionGuard>} />
                <Route path="/blacklist" element={<PermissionGuard resource="blacklist" action="view"><Blacklist /></PermissionGuard>} />
                <Route path="/change-password" element={<ChangePassword />} />
                <Route path="/broadcasts" element={<PermissionGuard resource="broadcasts" action="view"><BroadcastsList /></PermissionGuard>} />
                <Route path="/broadcasts/create" element={<PermissionGuard resource="broadcasts" action="create"><CreateBroadcast /></PermissionGuard>} />
                <Route path="/broadcasts/edit/:id" element={<PermissionGuard resource="broadcasts" action="edit"><EditBroadcast /></PermissionGuard>} />
                <Route path="/sales-site/settings" element={<PermissionGuard resource="salesSite" action="view" roles={['curator']}><SalesSiteSettings /></PermissionGuard>} />
                <Route path="/sales-site/design" element={<PermissionGuard resource="salesSite" action="view" roles={['curator']}><SalesSiteDesign /></PermissionGuard>} />
                <Route path="/sales-site/visual-editor" element={<PermissionGuard resource="salesSite" action="view" roles={['curator']}><SalesSiteVisualEditor /></PermissionGuard>} />
                <Route path="/sales-site/domains" element={<PermissionGuard resource="salesSite" action="view" roles={['curator']}><SalesSiteDomains /></PermissionGuard>} />
                <Route path="/deposit-admin" element={<PermissionGuard resource="salesSite" action="view" roles={['curator']}><DepositAdmin /></PermissionGuard>} />

              </Route>
            </Route>
            
            {/* Redirect to login if no route matches */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Router>
        </SnackbarProvider>
      </LocalizationProvider>
      </PanelThemeProvider>
    </AuthProvider>
  );
}

export default App;
